import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Link2,
  Plus,
  Copy,
  ExternalLink,
  BarChart3,
  MousePointer,
  Calendar,
  Trash2,
  Search,
  TrendingUp,
  Eye,
  Globe,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const LinkTrackerPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [stats, setStats] = useState({
    total_links: 0,
    total_clicks: 0,
    clicks_today: 0,
    top_performer: null
  });

  const [links, setLinks] = useState([]);

  const [newLink, setNewLink] = useState({
    name: '',
    original_url: '',
    custom_code: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [linksRes, statsRes] = await Promise.all([
        api.get('/links/'),
        api.get('/links/stats')
      ]);
      setLinks(linksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/links/', newLink);
      toast.success('Lien créé avec succès !');
      setShowCreateModal(false);
      setNewLink({ name: '', original_url: '', custom_code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const deleteLink = async (id) => {
    if (!window.confirm('Supprimer ce lien ?')) return;
    try {
      await api.delete(`/links/${id}`);
      toast.success('Lien supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredLinks = links.filter(l =>
    l.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.short_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Liens trackés
            </h1>
            <p className="text-[#64748b]">
              Créez et suivez vos liens courts avec analytics
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="rounded-full text-white"
            style={{ backgroundColor: theme.colors?.accent }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau lien
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                  <Link2 className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Total liens</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>{stats.total_links}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
                  <MousePointer className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Total clics</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>{(stats.total_clicks || 0).toLocaleString('fr-FR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Clics aujourd'hui</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>{stats.clicks_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Top performer</p>
                  <p className="text-sm font-bold truncate" style={{ color: theme.colors?.primary }}>{stats.top_performer || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
              <Input
                placeholder="Rechercher un lien..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-default pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Links List */}
        <Card className="card">
          <CardHeader>
            <CardTitle>Mes liens</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
              </div>
            ) : filteredLinks.length === 0 ? (
              <div className="text-center py-12">
                <Link2 className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
                <p className="text-[#64748b]">Aucun lien trouvé</p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 rounded-full text-white"
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer votre premier lien
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLinks.map((link) => (
                  <div key={link.id} className="p-4 rounded-xl border border-[#e2e8f0] hover:border-[#cbd5e1] transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium" style={{ color: theme.colors?.primary }}>{link.name}</h3>
                          <Badge className="bg-green-100 text-green-700">Actif</Badge>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <code 
                            className="px-2 py-1 rounded text-sm font-mono cursor-pointer hover:bg-[#e2e8f0] transition-colors"
                            style={{ backgroundColor: `${theme.colors?.accent}20`, color: theme.colors?.accent }}
                            onClick={() => copyToClipboard(link.short_url)}
                          >
                            {link.short_url}
                          </code>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => copyToClipboard(link.short_url)}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" asChild>
                            <a href={link.original_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </Button>
                        </div>
                        <p className="text-xs text-[#94a3b8] truncate">
                          <Globe className="w-3 h-3 inline mr-1" />
                          {link.original_url}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                            {(link.clicks || 0).toLocaleString('fr-FR')}
                          </p>
                          <p className="text-xs text-[#64748b]">Clics</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                            {(link.unique_clicks || 0).toLocaleString('fr-FR')}
                          </p>
                          <p className="text-xs text-[#64748b]">Uniques</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="rounded-full text-red-500" onClick={() => deleteLink(link.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#e2e8f0] text-xs text-[#94a3b8]">
                      <span><Calendar className="w-3 h-3 inline mr-1" />Créé le {link.created_at?.split('T')[0]}</span>
                      {link.last_click && <span><Eye className="w-3 h-3 inline mr-1" />Dernier clic: {link.last_click?.split('T')[0]}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent aria-describedby="create-link-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouveau lien tracké</DialogTitle>
            </DialogHeader>
            <p id="create-link-desc" className="sr-only">Créer un nouveau lien court avec tracking</p>
            <form onSubmit={handleCreateLink} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du lien *</Label>
                <Input
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  placeholder="Ex: Promo Noël 2024"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>URL de destination *</Label>
                <Input
                  value={newLink.original_url}
                  onChange={(e) => setNewLink({ ...newLink, original_url: e.target.value })}
                  placeholder="https://www.monsite.com/page"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Code personnalisé (optionnel)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#64748b]">msc.link/</span>
                  <Input
                    value={newLink.custom_code}
                    onChange={(e) => setNewLink({ ...newLink, custom_code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="mon-lien"
                    className="input-default flex-1"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white" 
                  style={{ backgroundColor: theme.colors?.accent }}
                  disabled={creating}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Créer le lien
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default LinkTrackerPage;
